import { EPaletteModes } from '@/types/theme-packs.types';
import { parseColor, resolveColor, formatTimestamp, getHeaderImage, getBtvFallbackForBg } from './utils/utils';
import type { TemplateProps } from './index';
import chroma from 'chroma-js';
import ContentEditable from './content-editable';
import { useImageUpload } from './utils/use-image-upload';
import styles from './template-styles.module.css';
import getRelativeContrastColor from '@/utils/colors/generate-relative-contrasting-color';

export const WesAnderson = ({
  event,
  theme,
  mode,
  calendarLinks,
  customizations,
  href,
  salutation,
  setSalutation,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  startIntro,
  setStartIntro,
  setHeaderImage,
}: TemplateProps) => {
  const resolvedMode = mode ?? event.settings.design.color_theme ?? theme.theme_mode ?? EPaletteModes.Light;
  const baseBg = resolvedMode === EPaletteModes.Dark ? '#000000' : '#FFFFFF';
  const themeColors = theme.colors[resolvedMode] ?? theme.colors;
  const bg =
    customizations?.backgroundColor ||
    chroma
      .mix(
        baseBg,
        customizations?.bodyTextColor || parseColor(themeColors.bodyTextColor, { opacity: 0.5 }),
        0.05,
        'rgb'
      )
      .css();
  const accent = resolveColor(themeColors.accentColor, customizations?.accentColor);
  const secondaryAccent = resolveColor(themeColors.secondaryAccentColor, customizations?.secondaryAccentColor);
  const secondaryAccentLight = customizations?.secondaryAccentColor;
  const secondaryBg = resolveColor(themeColors.secondaryBackgroundColor, customizations?.secondaryBackgroundColor);
  const originalHeading = customizations?.headingTextColor || parseColor(themeColors.headingTextColor);
  const originalBody = customizations?.bodyTextColor || parseColor(themeColors.bodyTextColor);
  const originalButtonTextColor =
    customizations?.buttonTextColor || parseColor(themeColors.buttonTextColor ?? themeColors.bodyTextColor);
  const heading = getRelativeContrastColor(originalHeading, accent);
  const headingOnSecondaryAccent = getRelativeContrastColor(originalHeading, secondaryAccent);
  const body = getRelativeContrastColor(originalBody, secondaryBg);
  const buttonTextColor = getRelativeContrastColor(originalButtonTextColor, body);

  const [loadingHeaderImg, uploadHeaderImg] = useImageUpload(setHeaderImage);
  const sessionDate = event.sessions[0]?.timestamp ? formatTimestamp(event.sessions[0].timestamp) : null;
  const anchorHref = href || '#';
  const headerImg =
    customizations?.headingImage || getHeaderImage(event) || getBtvFallbackForBg(secondaryAccentLight ?? bg);
  const headingFont = customizations?.headingFont
    ? `${customizations.headingFont}, sans-serif`
    : 'Arial Black, sans-serif';
  const bodyFont = customizations?.bodyFont ? `${customizations.bodyFont}, sans-serif` : 'Trebuchet MS, sans-serif';
  const _salutation = salutation ?? (customizations?.salutation || "You've been invited to:");
  const eventTitle = title ?? (customizations?.title || event.name);
  const eventDescription = description ?? customizations?.description;
  const _startIntro = startIntro ?? (customizations?.startIntro || 'The show starts on');
  const _cta = cta ?? (customizations?.cta || 'REGISTER');

  return (
    <table
      data-component="WesAnderson"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--accent': accent,
          '--secondary-accent': secondaryAccent,
          '--secondary-accent-light': secondaryAccentLight,
          '--secondary-bg': secondaryBg,
          '--body': body,
          '--heading': heading,
          '--heading-on-secondary-accent': headingOnSecondaryAccent,
          '--button-text': buttonTextColor,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '40px 40px 24px 40px' }}>
            <table
              width={520}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '520px', background: 'var(--bg)' }}
            >
              <tbody>
                <tr>
                  <td
                    align="center"
                    valign="middle"
                    height={48}
                    style={{ background: 'var(--secondary-accent-light)', padding: '8px' }}
                  >
                    {headerImg && (
                      <div
                        onClick={uploadHeaderImg}
                        data-cont="img"
                        className={loadingHeaderImg ? styles.imgLoading : undefined}
                      >
                        <img height={32} src={headerImg} style={{ display: 'block', margin: '0 auto' }} alt="" />
                      </div>
                    )}
                  </td>
                </tr>
                <tr>
                  <td align="center" style={{ background: 'var(--accent)', padding: '16px' }}>
                    <div
                      style={{
                        color: 'var(--heading)',
                        fontSize: '64px',
                        fontFamily: 'var(--heading-font)',
                        fontWeight: 900,
                        textTransform: 'uppercase',
                        lineHeight: '64px',
                        wordWrap: 'break-word',
                        textAlign: 'center',
                        maxWidth: '492px',
                      }}
                    >
                      INVITATION
                      <br />
                      INVITATION
                      <br />
                      INVITATION
                      <br />
                      INVITATION
                    </div>
                  </td>
                </tr>
                <tr>
                  <td align="center" style={{ background: 'var(--secondary-accent)', padding: '24px 8px' }}>
                    <ContentEditable
                      value={_salutation}
                      setValue={setSalutation}
                      style={{
                        color: 'var(--heading-on-secondary-accent)',
                        fontSize: '12px',
                        fontFamily: 'var(--body-font)',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        lineHeight: '16px',
                        letterSpacing: '0.36px',
                        wordWrap: 'break-word',
                        textAlign: 'center',
                        marginBottom: '8px',
                      }}
                    />
                    <ContentEditable
                      value={eventTitle}
                      setValue={setTitle}
                      style={{
                        color: 'var(--heading-on-secondary-accent)',
                        fontSize: '32px',
                        fontFamily: 'var(--heading-font)',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        lineHeight: '32px',
                        letterSpacing: '0.96px',
                        wordWrap: 'break-word',
                        textAlign: 'center',
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td align="center" style={{ background: 'var(--secondary-bg)', padding: '40px 24px' }}>
                    <table cellPadding={0} cellSpacing={0} border={0} role="presentation" style={{ maxWidth: '294px' }}>
                      <tbody>
                        {sessionDate && (
                          <>
                            <tr>
                              <td align="center" style={{ padding: '4px 8px 2px 8px' }}>
                                <ContentEditable
                                  value={_startIntro}
                                  setValue={setStartIntro}
                                  style={{
                                    color: 'var(--body)',
                                    fontSize: '12px',
                                    fontFamily: 'var(--body-font)',
                                    fontWeight: 400,
                                    textTransform: 'uppercase',
                                    lineHeight: '16px',
                                    letterSpacing: '0.36px',
                                    wordWrap: 'break-word',
                                    textAlign: 'center',
                                  }}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td align="center" style={{ padding: '4px 8px 16px 8px' }}>
                                <div
                                  style={{
                                    color: 'var(--body)',
                                    fontSize: '16px',
                                    fontFamily: 'var(--body-font)',
                                    fontWeight: 500,
                                    lineHeight: '20px',
                                    letterSpacing: '0.8px',
                                    wordWrap: 'break-word',
                                    textAlign: 'center',
                                    border: '1px solid var(--body)',
                                    padding: '4px 8px',
                                  }}
                                >
                                  {sessionDate}
                                </div>
                              </td>
                            </tr>
                          </>
                        )}
                        {eventDescription && (
                          <tr>
                            <td align="center" style={{ padding: '0 8px 24px 8px' }}>
                              <ContentEditable
                                value={eventDescription}
                                setValue={setDescription}
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '14px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 400,
                                  lineHeight: '20px',
                                  letterSpacing: '0.42px',
                                  wordWrap: 'break-word',
                                  textAlign: 'center',
                                }}
                              />
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td align="center">
                            <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                              <tbody>
                                <tr>
                                  <td
                                    align="center"
                                    valign="middle"
                                    style={{ background: 'var(--body)', width: '148px', height: '40px' }}
                                  >
                                    <a
                                      href={anchorHref}
                                      onClick={(e) => e.preventDefault()}
                                      style={{
                                        textDecoration: 'none',
                                        padding: '6px 0 6px 0',
                                        width: '148px',
                                        height: '100%',
                                        textAlign: 'center',
                                      }}
                                    >
                                      <ContentEditable
                                        value={_cta}
                                        setValue={setCta}
                                        style={{
                                          color: 'var(--button-text)',
                                          fontSize: '13px',
                                          fontFamily: 'var(--body-font)',
                                          fontWeight: 600,
                                          lineHeight: '16px',
                                          letterSpacing: '0.39px',
                                          wordWrap: 'break-word',
                                        }}
                                      />
                                    </a>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
            <table cellPadding={0} cellSpacing={0} border={0} role="presentation" style={{ marginTop: '40px' }}>
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.iCal}
                      >
                        iCal
                      </a>
                    </td>
                    <td
                      style={{
                        padding: '0 4px',
                        color: 'var(--body)',
                        fontSize: '11px',
                        fontFamily: "'TASA Orbiter', sans-serif",
                        fontWeight: 600,
                        lineHeight: '16px',
                        letterSpacing: '0.22px',
                      }}
                    >
                      •
                    </td>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.outlook}
                      >
                        Outlook
                      </a>
                    </td>
                    <td
                      style={{
                        padding: '0 4px',
                        color: 'var(--body)',
                        fontSize: '11px',
                        fontFamily: "'TASA Orbiter', sans-serif",
                        fontWeight: 600,
                        lineHeight: '16px',
                        letterSpacing: '0.22px',
                      }}
                    >
                      •
                    </td>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.google}
                      >
                        Google
                      </a>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
